/**
 * @Author: Nemo
 * @Description: 常用校验规则
 * @Date: 2024-05-18
 * @FilePath: /rhcn-checkout-web/src/plugins/validator/rules/common.js
 */

import { i18n } from '@/language/index';
import { identityNumberLabel } from '@/utils/tools/utils';

// Luhn算法验证卡号
const luhn = (cardNumber) => {
  let total = 0;
  const cardNumberLength = cardNumber.length;
  if (cardNumberLength < 13 || cardNumberLength > 19) {
    return false;
  }
  const maxLength = cardNumberLength + 1;
  for (let i = 1; i < maxLength; i++) {
    let num = parseInt(cardNumber.substring(cardNumberLength - i, cardNumberLength - i + 1));
    if (i % 2 === 0) {
      num *= 2;
      if (num < 10) {
        total += num;
      } else {
        var t1 = num + '';
        total += parseInt(t1.substring(0, 1)) + parseInt(t1.substring(1, 2));
      }
    } else {
      total += num;
    }
  }
  return total % 10 === 0;
};

// 验证CPF
const validarCPF = (cpf) => {
  const cpfDigits = cpf.replace(/[^\d]/g, ''); // 移除所有非数字字符
  if (cpfDigits.length !== 11) {
    return false;
  }
  // 检查重复数字
  if (/(\d)\1{10}/.test(cpfDigits)) {
    return false;
  }
  // 验证算法
  for (let t = 9; t < 11; t++) {
    let d = 0;
    for (var c = 0; c < t; c++) {
      d += parseInt(cpfDigits.charAt(c)) * ((t + 1) - c);
    }
    d = ((10 * d) % 11) % 10;
    if (parseInt(cpfDigits.charAt(c)) !== d) {
      return false;
    }
  }
  return true;
};

// 验证CNPJ
const validarCNPJ = (cnpj) => {
  const cnpjDigits = cnpj.replace(/[^\d]/g, ''); // 移除所有非数字字符
  if (cnpjDigits.length !== 14) {
    return false;
  }
  let size = cnpjDigits.length - 2;
  let numbers = cnpjDigits.substring(0, size);
  const digits = cnpjDigits.substring(size);
  let sum = 0;
  let pos = size - 7;
  for (let i = size; i >= 1; i--) {
    sum += parseInt(numbers.charAt(size - i)) * pos;
    pos = pos - 1;
    if (pos < 2) {
      pos = 9;
    }
  }
  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (parseInt(result) !== parseInt(digits.charAt(0))) {
    return false;
  }
  size = size + 1;
  numbers = cnpjDigits.substring(0, size);
  sum = 0;
  pos = size - 7;
  for (let i = size; i >= 1; i--) {
    sum += parseInt(numbers.charAt(size - i)) * pos;
    pos = pos - 1;
    if (pos < 2) {
      pos = 9;
    }
  }
  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (parseInt(result) !== parseInt(digits.charAt(1))) {
    return false;
  }
  return true;
};

// 必填
export const required = {
  validate: value => value !== undefined && value !== null && value !== '',
  // 默认不显示错误信息，现在必填message特殊处理放在右上角
  message: i18n => ' ',
};

// 卡号
export const cardNumber = {
  validate: value => {
    if (!value) return true;
    const num = value.replace(/\s*/g, '');
    return luhn(num);
  },
  message: i18n => i18n.t('message.validator.input', { field: i18n.t('message.checkout.cardNumber').toLowerCase() }),
};

// 有效期 (MM/YY 或 MM/YYYY)
export const monthYear = {
  validate: value => {
    if (!value) return true;

    const val = value;
    const n = new Date();
    const y = n.getFullYear();
    const m = n.getMonth() + 1;

    if (val.indexOf('/') <= 1) return false;

    const myArray = val.split('/');
    const month = Number.parseInt(myArray[0]);
    const year = Number.parseInt(myArray[1]);

    // 格式检查：月份和年份格式
    if (myArray.length !== 2 ||
        (myArray[1].length !== 2 && myArray[1].length !== 4) ||
        myArray[0].length !== 2 ||
        month > 12) {
      return false;
    }

    // 四位数年份的检查
    if (myArray[1].length === 4) {
      return y < year || (y === year && m <= month);
    }

    // 两位数年份的检查
    if ((y % 100 < year) && ((year - (y % 100)) < 25)) {
      return true; // 年份比现在大
    } else if (y % 100 === year && m <= month) {
      return true; // 月份比现在大
    } else if (((year - (y % 100)) === 25) && m >= month) {
      return true;
    }

    return false;
  },
  message: i18n => i18n.t('message.validator.input', { field: i18n.t('message.checkout.expiredDate').toLowerCase() }),
};

// CVV
export const cvv = {
  validate: value => {
    if (!value) return true;
    const val = (value || '').trim();
    return val.length >= 3;
  },
  message: i18n => i18n.t('message.validator.input', { field: i18n.t('message.checkout.cvv').toLowerCase() }),
};

// 持卡人姓名
export const holderName = {
  validate: value => {
    if (!value) return true;
    const val = value.trim();
    const valArr = val.split(' ');

    return valArr.length >= 2;
  },
  message: i18n => i18n.t('message.validator.input', { field: i18n.t('message.checkout.holderName').toLowerCase() }),
};

// 邮箱
export const email = {
  validate: value => {
    if (!value) return true;
    const val = value.trim();
    const regex = /^[a-zA-Z0-9]+[^@]*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/;
    return regex.test(val);
  },
  message: i18n => i18n.t('message.validator.input', { field: i18n.t('message.checkout.email').toLowerCase() }),
};

// 出生日期
export const birthDate = {
  validate: value => {
    if (!value) return true;

    const val = value.trim();
    // 适配新格式 YYYY / MM / DD
    const dateArr = val.split(' / ');

    if (dateArr.length !== 3) return false;

    const regex = /^\d*$/;
    if (!regex.test(dateArr[0]) || !regex.test(dateArr[1]) || !regex.test(dateArr[2])) {
      return false;
    }

    if (dateArr[0].length !== 4 || dateArr[1].length !== 2 || dateArr[2].length !== 2 ||
        parseInt(dateArr[1]) > 12 || parseInt(dateArr[2]) > 31 || parseInt(dateArr[1]) < 1 || parseInt(dateArr[2]) < 1) {
      return false;
    }

    return !isNaN(new Date(dateArr[0], parseInt(dateArr[1]) - 1, dateArr[2]).getFullYear());
  },
  message: i18n => i18n.t('message.validator.input', { field: i18n.t('message.checkout.birthDate').toLowerCase() }),
};

// 发卡日期
export const issueDate = {
  validate: value => {
    if (!value) return true;

    const val = value.trim();
    if (val.indexOf('/') <= 1) return false;

    const myArray = val.split('/');
    const month = Number.parseInt(myArray[0]);

    return myArray.length === 2 &&
           (myArray[1].length === 2 || myArray[1].length === 4) &&
           myArray[0].length === 2 &&
           month <= 12;
  },
  message: i18n => i18n.t('message.validator.input', { field: i18n.t('message.checkout.issueDate').toLowerCase() }),
};

// 身份证号必填规则
export const identityNumberRequired = {
  validate: value => value !== undefined && value !== null && value !== '',
  message: (i18n, { param1 }) => {
    const country = param1;
    const fieldName = identityNumberLabel[country] || 'document';
    return i18n.t('message.validator.input', { field: fieldName });
  },
};

// 身份证号 (需要国家参数)
export const identityNumber = {
  validate: (value, country) => {
    if (!value) return true;

    const newValue = value.trim();

    if (country === 'BR') {
      const idNum = newValue.replace(/[^\d]/g, '');
      if (idNum.length < 11 || idNum.length > 14) {
        return false;
      }
      return validarCPF(idNum) || validarCNPJ(idNum);
    } else if (country === 'CL') {
      return newValue.length >= 8 && newValue.length <= 9;
    } else if (country === 'CO') {
      return newValue.length >= 6 && newValue.length <= 10;
    } else if (country === 'PE') {
      return newValue.length >= 8 && newValue.length <= 9;
    } else if (country === 'UY') {
      return (newValue.length >= 6 && newValue.length <= 8) || newValue.length === 12;
    }

    return true;
  },
  message: (i18n, { param1 }) => {
    const country = param1;
    const fieldName = identityNumberLabel[country] || 'document';
    return i18n.t('message.validator.input', { field: fieldName });
  },
};

// BLIK码
export const blikcode = {
  validate: value => {
    if (!value) return true;
    return value.length === 6 && /^[0-9]+$/.test(value);
  },
  message: i18n => i18n.t('message.validator.input', { field: i18n.t('message.checkout.blikcode').toLowerCase() }),
};
